"use client";

import Link from "next/link";
import { useState } from "react";
import FAIcon from "../icons/faIcon";
import Image from "next/image";

function RegionColumn({ region }) {
  const visibleTowns = region.towns.slice(0, 5);
  const hiddenCount = Math.max(0, region.towns.length - visibleTowns.length);

  return (
    <div className="flex flex-col items-center px-3 text-center">
      <Link
        href={region.href}
        className="font-raleway text-sm font-semibold leading-tight text-white/92 transition-colors duration-200 hover:text-white"
      >
        {region.name}
      </Link>

      <div className="mt-3 flex flex-col gap-2">
        {visibleTowns.map((town) => (
          <Link
            key={`${region.name}-${town.name}`}
            href={town.href}
            className="font-raleway text-sm leading-tight text-white/62 transition-colors duration-200 hover:text-white"
          >
            {town.name}
          </Link>
        ))}

        {hiddenCount > 0 ? (
          <Link
            href={region.href}
            className="font-raleway text-sm italic leading-tight text-white/54 transition-colors duration-200 hover:text-white"
          >
            + {hiddenCount} more
          </Link>
        ) : null}
      </div>
    </div>
  );
}

export default function FooterLocationsSection({ regions = [] }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const totalTowns = regions.reduce(
    (sum, region) => sum + (region.towns?.length ?? 0),
    0,
  );

  if (!regions.length) {
    return null;
  }

  return (
    <section className="relative z-10  border-white/12 pt-12">
      <div className="mx-auto flex max-w-full flex-col items-center text-center">
        <div className="flex items-center gap-3 text-primary">
          <FAIcon icon="location-dot" size={18} noMargin />
          <p className="font-raleway text-sm font-semibold uppercase tracking-[0.18em] text-primary">
            Coverage Locations
          </p>
        </div>

        <h3 className="mt-4 max-w-4xl font-raleway text-[2rem] font-semibold leading-[0.94] tracking-[-0.055em] text-white sm:text-[2.5rem]">
          See some of the areas we support.
        </h3>

        <p className="mt-4 max-w-3xl text-sm leading-6 text-white/68 sm:text-[1rem] sm:leading-7">
          Browse all {regions.length} regions and {totalTowns} towns in SML's
          location coverage, then move into the areas, services and sectors most
          relevant to your business.
        </p>

        <div className="relative mt-10 w-full">
          <div
            id="footer-locations-grid"
            className={`grid w-full grid-cols-1 gap-x-6 gap-y-8 overflow-hidden transition-[max-height] duration-500 ease-in-out sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 ${
              isExpanded ? "max-h-500" : "max-h-92"
            }`}
          >
            {regions.map((region) => (
              <RegionColumn key={region.name} region={region} />
            ))}
          </div>

          {!isExpanded ? (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-linear-to-t from-accent via-accent/92 to-transparent" />
          ) : null}
        </div>

        <div className="mt-5 flex flex-col items-center gap-4 sm:flex-row sm:gap-6">
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-controls="footer-locations-grid"
            onClick={() => setIsExpanded((value) => !value)}
            className="inline-flex items-center gap-2 font-raleway text-xs font-semibold uppercase tracking-[0.16em] text-white/64 transition-colors duration-200 hover:text-white"
          >
            {isExpanded ? "Show fewer locations" : "Show all locations"}
            <FAIcon
              icon={isExpanded ? "chevron-up" : "chevron-down"}
              size={11}
              noMargin
            />
          </button>

          <Link
            href="/locations"
            className="inline-flex items-center gap-2 font-raleway text-xs font-semibold uppercase tracking-[0.16em] text-white/64 transition-colors duration-200 hover:text-white"
          >
            View all locations
            <FAIcon icon="arrow-right" size={11} noMargin />
          </Link>
        </div>
          <Link
              href="https://crushedice.uk"
              target="_blank"
              rel="noreferrer"
              className="flex items-center mx-auto gap-1.5 text-white/72 transition-colors duration-200 hover:text-white mt-12 -mb-10"
            >
              <span className="font-raleway text-[16px] leading-tight tracking-[-0.03em] sm:text-md">
                Website built by
              </span>
              <Image
                src="/images/logo/crushed-ice-icon.svg"
                width={13}
                height={13}
                alt="Crushed Ice Logo"
                className="brightness-0 invert mt-0.5"
              />
              <span className="font-raleway text-[16px] leading-tight tracking-[-0.03em] sm:text-md">
                Crushed Ice
              </span>
            </Link>
      </div>
    </section>
  );
}
